import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Divider,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  VisibilityOutlined as ViewIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  AddOutlined as AddIcon,
  DownloadOutlined as DownloadIcon,
  CloudDownloadOutlined as CloudDownloadIcon,
  VisibilityOff as VisibilityOffIcon,
  Code as CodeIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import api from '../services/api';
import PageHeader from '../components/common/PageHeader';
import { format } from 'date-fns';

// Action type to icon mapping
const actionIcons = {
  'VIEW': <ViewIcon fontSize="small" />,
  'CREATE': <AddIcon fontSize="small" />,
  'UPDATE': <EditIcon fontSize="small" />,
  'DELETE': <DeleteIcon fontSize="small" />,
  'DOWNLOAD': <DownloadIcon fontSize="small" />,
  'EXPORT': <CloudDownloadIcon fontSize="small" />,
  'EMAIL_NOTIFICATION': <EmailIcon fontSize="small" />,
};

// Action type to color mapping
const actionColors = {
  'VIEW': 'info',
  'CREATE': 'success',
  'UPDATE': 'warning',
  'DELETE': 'error',
  'DOWNLOAD': 'secondary',
  'EXPORT': 'secondary',
  'EMAIL_NOTIFICATION': 'primary',
};

const AuditLogs = () => {
  const theme = useTheme();
  
  // States
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogs, setTotalLogs] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [actionsList, setActionsList] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showRawJson, setShowRawJson] = useState(false);
  const [logType, setLogType] = useState('all'); // 'all', 'audit', 'email'

  // Fetch audit logs data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      };

      // Add filters if they exist
      if (userFilter) params.username = userFilter;
      if (actionFilter) params.action_type = actionFilter;
      if (startDate && endDate) {
        params.from_date = format(startDate, 'yyyy-MM-dd');
        params.to_date = format(endDate, 'yyyy-MM-dd');
      }
      if (logType) params.log_type = logType;

      // Fetch logs - use combined endpoint to get both audit logs and email notifications
      const response = await api.get('/api/combined-logs', { params });
      setLogs(response.data.logs || []);
      setTotalLogs(response.data.total || 0);
      
      // Set filter options
      if (response.data.filters) {
        setUsersList(response.data.filters.usernames || []);
        setActionsList(response.data.filters.action_types || []);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs. Please try again.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, userFilter, actionFilter, startDate, endDate, refreshTrigger, logType]);

  // Initial data fetch and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle filter changes
  const handleUserFilterChange = (e) => {
    setUserFilter(e.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleActionFilterChange = (e) => {
    setActionFilter(e.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle date filter changes
  const handleStartDateChange = (date) => {
    setStartDate(date);
    setPage(0); // Reset to first page when filter changes
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle opening details dialog
  const handleOpenDetails = (log) => {
    setSelectedLog(log);
    setDetailsDialog(true);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return format(date, 'MMM dd, yyyy HH:mm:ss');
    } catch (e) {
      return timestamp;
    }
  };

  // Get action icon
  const getActionIcon = (actionType) => {
    return actionIcons[actionType] || <HistoryIcon fontSize="small" />;
  };

  // Get action color
  const getActionColor = (actionType) => {
    return actionColors[actionType] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader 
        title="Audit Logs" 
        subtitle="Track and monitor user activities across the system"
        icon={HistoryIcon}
      />
      
      {/* Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: theme.palette.background.card }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HistoryIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Activities</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {loading ? <CircularProgress size={24} /> : totalLogs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: theme.palette.background.card }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Unique Users</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {loading ? <CircularProgress size={24} /> : usersList.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: theme.palette.background.card }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CategoryIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">Action Types</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {loading ? <CircularProgress size={24} /> : actionsList.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: theme.palette.background.card }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EventIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Date Range</Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {startDate && endDate ? (
                    `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd, yyyy')}`
                  ) : 'All Time'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* Filters */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: theme.palette.background.card,
          borderRadius: 2
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search in resources..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select
                value={userFilter}
                onChange={handleUserFilterChange}
                label="User"
              >
                <MenuItem value="">All Users</MenuItem>
                {usersList.map((user) => (
                  <MenuItem key={user} value={user}>{user}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Action Type</InputLabel>
              <Select
                value={actionFilter}
                onChange={handleActionFilterChange}
                label="Action Type"
              >
                <MenuItem value="">All Actions</MenuItem>
                {actionsList.map((action) => (
                  <MenuItem key={action} value={action}>{action}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Log Type</InputLabel>
              <Select
                value={logType}
                onChange={(e) => setLogType(e.target.value)}
                label="Log Type"
              >
                <MenuItem value="all">All Logs</MenuItem>
                <MenuItem value="audit">Audit Logs Only</MenuItem>
                <MenuItem value="email">Email Notifications Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                maxDate={endDate || undefined}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                minDate={startDate || undefined}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={1}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {/* Audit Logs Table */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: theme.palette.background.card 
        }}
      >
        <TableContainer component={Box}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading audit logs...</Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1">No audit logs found</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Try adjusting your filters or search terms
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs
                  .filter(log => 
                    !searchTerm || 
                    log.resource?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    log.username?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((log, index) => (
                    <TableRow 
                      key={index}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        }
                      }}
                    >
                      <TableCell>
                        <Chip
                          icon={getActionIcon(log.action_type)}
                          label={log.action_type}
                          color={getActionColor(log.action_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                          <Typography variant="body2">{log.username}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {log.resource}
                          </Typography>
                          {log.type === 'email_notification' && log.details && (
                            <Typography 
                              variant="caption" 
                              color="textSecondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              Region: {log.details.region} • {log.details.days_until_expiration} days remaining
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDetails(log)}
                            color="primary"
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalLogs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Details Dialog */}
      <Dialog 
        open={detailsDialog} 
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedLog && getActionIcon(selectedLog.action_type)}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {selectedLog?.type === 'email_notification' ? 'Email Notification Details' : 'Audit Log Details'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  {selectedLog.type === 'email_notification' ? 'Recipient Email' : 'User'}
                </Typography>
                <Typography variant="body1">{selectedLog.username}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Action Type</Typography>
                <Typography variant="body1">{selectedLog.action_type}</Typography>
              </Grid>
              {selectedLog.type === 'email_notification' && selectedLog.details && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Client Name</Typography>
                    <Typography variant="body1">{selectedLog.details.client_name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Region</Typography>
                    <Typography variant="body1">{selectedLog.details.region || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Notification Interval</Typography>
                    <Typography variant="body1">{selectedLog.details.notification_interval} days before expiration</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Days Until Expiration</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: selectedLog.details.days_until_expiration <= 30 ? 'error.main' : 'primary.main' }}>
                      {selectedLog.details.days_until_expiration} days
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Expiration Date</Typography>
                    <Typography variant="body1">
                      {selectedLog.details.expiration_date ? formatTimestamp(selectedLog.details.expiration_date) : 'N/A'}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Resource</Typography>
                <Typography variant="body1">{selectedLog.resource}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Timestamp</Typography>
                <Typography variant="body1">{formatTimestamp(selectedLog.timestamp)}</Typography>
              </Grid>
              {selectedLog.type !== 'email_notification' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">IP Address</Typography>
                  <Typography variant="body1">{selectedLog.ip_address || 'N/A'}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Details</Typography>
                {selectedLog && selectedLog.action_type === 'UPDATE' && selectedLog.details && selectedLog.details.previous_data ? (
                  <Box>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        mt: 1, 
                        mb: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        maxHeight: 400,
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Changes Made:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Define field mapping between snake_case and camelCase */}
                        {(() => {
                          // Create a mapping between different field naming conventions
                          const fieldMappings = {
                            // request_data (camelCase) to previous_data (snake_case)
                            'associatedLogData': 'associated_log_data',
                            'startDate': 'start_date',
                            'endDate': 'end_date',
                            'integrationDate': 'integration_date',
                            'licensePerDay': 'license_per_day',
                            'onlineRetention': 'online_retention',
                            'offlineRetention': 'offline_retention',
                            'devicesToIntegrate': 'devices_to_integrate',
                            'contractRenewed': 'contract_renewed',
                            'customerDashboard': 'customer_dashboard'
                          };
                          
                          // Field display names for better readability
                          const fieldDisplayNames = {
                            'associatedLogData': 'Associated Log Data',
                            'startDate': 'Start Date',
                            'endDate': 'End Date',
                            'integrationDate': 'Integration Date',
                            'licensePerDay': 'License Per Day',
                            'onlineRetention': 'Online Retention',
                            'offlineRetention': 'Offline Retention',
                            'devicesToIntegrate': 'Devices To Integrate',
                            'contractRenewed': 'Contract Renewed',
                            'customerDashboard': 'Customer Dashboard'
                          };
                          
                          // Format values for display
                          const formatValue = (val) => {
                            if (val === null || val === undefined) return 'Not set';
                            if (val === '') return 'Not set';
                            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                            
                            // Format date strings
                            if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T/)) {
                              return new Date(val).toLocaleDateString();
                            }
                            
                            if (typeof val === 'object') return JSON.stringify(val);
                            return String(val);
                          };
                          
                          // Process each field in request_data
                          return Object.keys(selectedLog.details.request_data || {}).map(requestKey => {
                            const newValue = selectedLog.details.request_data[requestKey];
                            const previousKey = fieldMappings[requestKey] || requestKey;
                            const oldValue = selectedLog.details.previous_data[previousKey];
                            
                            // Skip internal fields
                            if (requestKey.startsWith('_')) return null;
                            
                            // Get display name
                            const displayKey = fieldDisplayNames[requestKey] || 
                              requestKey.replace(/([A-Z])/g, ' $1')
                                      .replace(/_/g, ' ')
                                      .replace(/^\w/, c => c.toUpperCase());
                            
                            // Convert values for comparison
                            const normalizedOldValue = oldValue !== undefined ? String(oldValue).replace(/T00:00:00(\.000Z)?$/, '') : undefined;
                            const normalizedNewValue = newValue !== undefined ? String(newValue) : undefined;
                            
                            // Check if values are different for display
                            const hasChanged = normalizedOldValue !== normalizedNewValue;
                            
                            if (!hasChanged) return null;
                            
                            return (
                              <Box key={requestKey} sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                p: 1,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.background.default, 0.5)
                              }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  {displayKey}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 2 }}>
                                  <Box sx={{ 
                                    flex: 1,
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.error.light, 0.1),
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                                  }}>
                                    <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>
                                      Previous Value:
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                      {formatValue(oldValue)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 1
                                  }}>
                                    <Typography variant="body2" color="text.secondary">→</Typography>
                                  </Box>
                                  <Box sx={{ 
                                    flex: 1,
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.success.light, 0.1),
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                  }}>
                                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                                      New Value:
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                      {formatValue(newValue)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          });
                        })()}
                      </Box>
                    </Paper>
                  </Box>
                ) : (
                  selectedLog && (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        mt: 1, 
                        mb: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        maxHeight: 400,
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {selectedLog.action_type === 'VIEW' ? 'View Details' : 
                         selectedLog.action_type === 'DELETE' ? 'Deletion Details' : 
                         selectedLog.action_type === 'CREATE' ? 'Creation Details' : 
                         `${selectedLog.action_type} Details`}
                      </Typography>
                      
                      {/* Method */}
                      {selectedLog.details.method && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Method:
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            display: 'inline-block',
                            ml: 1,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 'bold'
                          }}>
                            {selectedLog.details.method}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Status Code */}
                      {selectedLog.details.status_code && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            Status:
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            display: 'inline-block',
                            ml: 1,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: selectedLog.details.status_code >= 400 
                              ? alpha(theme.palette.error.main, 0.1)
                              : alpha(theme.palette.success.main, 0.1),
                            color: selectedLog.details.status_code >= 400 
                              ? theme.palette.error.main
                              : theme.palette.success.main,
                            fontWeight: 'bold'
                          }}>
                            {selectedLog.details.status_code}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Request Data */}
                      {selectedLog.details.request_data && Object.keys(selectedLog.details.request_data).length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Request Data:
                          </Typography>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 1, 
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                          }}>
                            {Object.entries(selectedLog.details.request_data).map(([key, value]) => {
                              // Skip internal fields or empty objects
                              if (key.startsWith('_') || (typeof value === 'object' && value !== null && Object.keys(value).length === 0)) {
                                return null;
                              }
                              
                              // Format the key for display
                              const displayKey = key
                                .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                                .replace(/_/g, ' ') // Replace underscores with spaces
                                .replace(/^\w/, c => c.toUpperCase()); // Capitalize first letter
                              
                              // Format value for display
                              const formatValue = (val) => {
                                if (val === null || val === undefined) return 'Not set';
                                if (val === '') return 'Not set';
                                if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                                
                                // Format date strings
                                if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T/)) {
                                  return new Date(val).toLocaleDateString();
                                }
                                
                                if (typeof val === 'object') {
                                  return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>{JSON.stringify(val, null, 2)}</pre>;
                                }
                                
                                return String(val);
                              };
                              
                              return (
                                <Box key={key} sx={{ 
                                  display: 'flex', 
                                  mb: 1.5,
                                  pb: 1.5,
                                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                  '&:last-child': {
                                    mb: 0,
                                    pb: 0,
                                    borderBottom: 'none'
                                  }
                                }}>
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 'bold', 
                                    minWidth: '180px',
                                    color: theme.palette.text.secondary
                                  }}>
                                    {displayKey}:
                                  </Typography>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {formatValue(value)}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Additional Details */}
                      {selectedLog.details.params && Object.keys(selectedLog.details.params).length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Parameters:
                          </Typography>
                          <Box sx={{ 
                            p: 2, 
                            borderRadius: 1, 
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                          }}>
                            {Object.entries(selectedLog.details.params).map(([key, value]) => (
                              <Box key={key} sx={{ display: 'flex', mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '120px' }}>
                                  {key}:
                                </Typography>
                                <Typography variant="body2">
                                  {String(value)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  )
                )}
                
                {/* Raw JSON Data (Hidden by default) */}
                <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    size="small" 
                    onClick={() => setShowRawJson(prev => !prev)}
                    startIcon={showRawJson ? <VisibilityOffIcon /> : <CodeIcon />}
                    color="secondary"
                    variant="outlined"
                  >
                    {showRawJson ? 'Hide Raw JSON' : 'Show Raw JSON'}
                  </Button>
                </Box>
                
                {showRawJson && (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      mt: 1, 
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                      maxHeight: 300,
                      overflow: 'auto',
                      fontFamily: 'monospace'
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedLog?.details, null, 2)}
                    </pre>
                  </Paper>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs; 